/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * @file readFileAsText.ts
 * @desc 读取文件文本内容并检测 UTF-8 编码
 */

export interface ReadFileResult {
    content: string;
    encoding: string; // 检测到的编码，例如 "utf-8" | "gbk" | "unknown"
}

/**
 * 粗略检测文件编码
 * - 检查 BOM 头
 * - 简单 UTF-8 校验（兼容 ASCII）
 */

function detectEncoding(uint8Array: Uint8Array): 'utf-8' | 'gbk' | 'unknown' {
    // UTF-8 BOM
    if (uint8Array[0] === 0xef && uint8Array[1] === 0xbb && uint8Array[2] === 0xbf) {
        return 'utf-8';
    }

    // 检测是否可能是 UTF-8（简单字节检查）
    let i = 0;
    while (i < uint8Array.length) {
        if (uint8Array[i] <= 0x7f) {
            // 单字节 ASCII
            i++;
        } else if (
            uint8Array[i] >= 0xc2
            && uint8Array[i] <= 0xdf
            && uint8Array[i + 1] >= 0x80
            && uint8Array[i + 1] <= 0xbf
        ) {
            i += 2;
        } else if (
            uint8Array[i] >= 0xe0
            && uint8Array[i] <= 0xef
            && uint8Array[i + 1] >= 0x80
            && uint8Array[i + 1] <= 0xbf
            && uint8Array[i + 2] >= 0x80
            && uint8Array[i + 2] <= 0xbf
        ) {
            i += 3;
        } else {
            // 不符合 UTF-8
            return 'gbk'; // 可能是 gbk 或其他编码
        }
    }

    return 'utf-8';
}


/**
 * 异步读取文件文本内容（默认 UTF-8），并检测是否为 UTF-8 编码
 * @param file File 对象
 */
export const readFileAsText = (file: File): Promise<ReadFileResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = e => {
            const arrayBuffer = e.target?.result;
            if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) {
                return reject(new Error('读取文件失败'));
            }

            // 检测编码
            const uint8Array = new Uint8Array(arrayBuffer);
            const encoding = detectEncoding(uint8Array);

            try {
                const decoder = new TextDecoder(encoding === 'utf-8' ? 'utf-8' : 'utf-8', {
                    fatal: false
                });
                const content = decoder.decode(uint8Array);
                resolve({content, encoding});
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = reject;

        // 用 ArrayBuffer 读取，以便检测编码
        reader.readAsArrayBuffer(file);
    });
};


export async function buildEvaluatorFiles(evaluatorStore): Promise<File[]> {
    const files: File[] = [];

    for (const fileInfo of evaluatorStore.evaluators) {
        let code = fileInfo.code;

        if (!code && fileInfo.bos_url) {
            const content = await evaluatorStore.fetchFileInfo(fileInfo);
            if (content) {
                code = content;
            }
        }

        const file = new File([code || ''], fileInfo.name);
        files.push(file);
    }

    return files;
}
